import type { GroupColor, GroupDetail, GroupIcon, GroupRef, PersonRef } from '@ralph/contracts'
import { DEFAULT_GROUP_ICON } from '@ralph/contracts'
import clsx from 'clsx'
import { Crown, PencilLine, Trash2, TriangleAlert, UserMinus, UserPlus, UsersRound } from 'lucide-react'
import { useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiRequestError } from '@/api/client.ts'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { leastUsedColor } from '@/components/groups/group-icons.ts'
import { ColorChooser, IconChooser } from '@/components/groups/IdentityChoosers.tsx'
import { type OwnerChoice, OwnerPicker, ownerRefOf } from '@/components/groups/OwnerPicker.tsx'
import { PersonPicker } from '@/components/people/PersonPicker.tsx'
import { Notice } from '@/components/ui/admin-chrome.tsx'
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/Dialog.tsx'
import { Button, Input, Spinner } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import {
  useAddGroupMembers,
  useAppointManager,
  useCreateGroup,
  useDeleteGroup,
  useGroupDeletionPreview,
  useGroups,
  useRemoveGroupMember,
  useRemoveManager,
  useUpdateGroup
} from '@/hooks/use-groups.ts'
import { issueIs, useI18n } from '@/i18n/index.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * The groups plane's dialogs (PRD-26 *The interface*).
 *
 * Every refusal is the server's. A name already taken, a group changed under an editor, a digest
 * that drifted while an administrator read the deletion's consequences — each arrives as a code
 * and each has a designed state here; a disabled button may agree with the server but never
 * substitutes for it.
 */

const messageOf = (t: Messages['groups'], error: unknown): string =>
  error instanceof ApiRequestError ? (error.issues[0]?.message ?? t.genericError) : t.genericError

/** A refusal about the name itself (its length, its characters): said on the field, not the form. */
const aboutName = (error: unknown): boolean => error instanceof ApiRequestError && error.issues[0]?.field === 'name'

// ---------------------------------------------------------------------------
// New group, and editing one
// ---------------------------------------------------------------------------

const IdentityFields = ({
  name,
  icon,
  color,
  onName,
  onIcon,
  onColor,
  error
}: {
  name: string
  icon: GroupIcon
  color: GroupColor
  onName: (value: string) => void
  onIcon: (value: GroupIcon) => void
  onColor: (value: GroupColor) => void
  error: string | null
}) => {
  const t = useI18n().t.groups
  const nameId = useId()
  const hintId = useId()
  const iconLabelId = useId()
  const colorLabelId = useId()
  const trimmed = name.trim()
  return (
    <>
      <div className="flex items-center gap-3 rounded-tile border border-rule bg-fg/[0.02] px-3 py-3">
        <GroupAvatar group={{ icon, color }} size={44} />
        <div className="min-w-0">
          <p className="eyebrow">{t.create.preview}</p>
          {/* The sample name in the muted ink until one is typed, so it reads as an example
              rather than as a name already chosen. */}
          <p
            className={clsx(
              'truncate text-[15px] font-semibold tracking-[-0.016em]',
              trimmed === '' && 'text-fg-subtle'
            )}
          >
            {trimmed === '' ? t.create.namePlaceholder : trimmed}
          </p>
        </div>
      </div>

      <div>
        <label htmlFor={nameId} className="mb-1 block text-[13px] font-semibold">
          {t.create.name}
        </label>
        <Input
          id={nameId}
          value={name}
          maxLength={60}
          aria-describedby={error === null ? hintId : `${hintId} ${hintId}-error`}
          aria-invalid={error !== null}
          placeholder={t.create.namePlaceholder}
          onChange={(event) => onName(event.target.value)}
        />
        <p id={hintId} className="mt-1 text-[12px] text-fg-subtle">
          {t.create.nameHint}
        </p>
        {/* Only what is wrong with the name; anything else is the form's alert, not this field's. */}
        {error !== null && (
          <p id={`${hintId}-error`} role="alert" className="mt-1 text-[12px] text-alert">
            {error}
          </p>
        )}
      </div>

      <div>
        <p id={iconLabelId} className="mb-1.5 text-[13px] font-semibold">
          {t.create.icon}
        </p>
        <IconChooser value={icon} color={color} onChange={onIcon} labelledBy={iconLabelId} />
      </div>

      <div>
        <p id={colorLabelId} className="mb-1.5 text-[13px] font-semibold">
          {t.create.color}
        </p>
        <ColorChooser value={color} onChange={onColor} labelledBy={colorLabelId} />
      </div>
    </>
  )
}

export const CreateGroupDialog = ({ onClose }: { onClose: () => void }) => {
  const t = useI18n().t.groups
  const titleId = useId()
  const navigate = useNavigate()
  const create = useCreateGroup()
  const [name, setName] = useState('')
  const [icon, setIcon] = useState<GroupIcon>(DEFAULT_GROUP_ICON)
  // The server picks the least used colour when none is sent (D-6); the preview needs one now, so
  // the same rule runs here over the groups there are — until somebody chooses.
  const existing = useGroups({ scope: 'all', limit: 200 })
  const [chosenColor, setColor] = useState<GroupColor | null>(null)
  const color = chosenColor ?? leastUsedColor(existing.data?.items ?? [])
  const [managers, setManagers] = useState<PersonRef[]>([])

  // `issueIs`, not `code === 'conflict'`. The server raises a conflict for THREE different
  // facts here — the name is taken, the group could not be created, somebody else changed it
  // while you were editing — and branching on the code reported all three as "that name is
  // taken", which sent a person to edit a name that was never the problem. The key names the
  // sentence; the code only names the class (PRD-27 D-4, D-6).
  const taken = issueIs(create.error, 'groups.name_taken')
  const nameError = taken ? t.create.taken : aboutName(create.error) ? messageOf(t, create.error) : null
  const formError = create.isError && nameError === null ? messageOf(t, create.error) : null

  const submit = () =>
    create.mutate(
      { name: name.trim(), icon, color, managerIds: managers.map((person) => person.id) },
      {
        onSuccess: (group) => {
          onClose()
          navigate(`/groups/${group.id}`)
        }
      }
    )

  return (
    <Dialog
      titleId={titleId}
      icon={<UsersRound size={14} />}
      title={t.create.title}
      lead={t.create.lead}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (name.trim() !== '') submit()
        }}
      >
        <DialogBody>
          {formError && <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={formError} />}
          <IdentityFields
            name={name}
            icon={icon}
            color={color}
            onName={setName}
            onIcon={setIcon}
            onColor={setColor}
            error={nameError}
          />
          <div>
            <p className="mb-1 text-[13px] font-semibold">{t.create.managers}</p>
            <p className="mb-2 text-[12px] text-fg-subtle">{t.create.managersHint}</p>
            <PersonPicker chosen={managers} onChange={setManagers} multiple />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t.create.cancel}
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending || name.trim() === ''}>
            {create.isPending ? t.create.submitting : t.create.submit}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

export const EditGroupDialog = ({ group, onClose }: { group: GroupDetail; onClose: () => void }) => {
  const t = useI18n().t.groups
  const titleId = useId()
  const update = useUpdateGroup()
  const [name, setName] = useState(group.name)
  const [icon, setIcon] = useState<GroupIcon>(group.icon)
  const [color, setColor] = useState<GroupColor>(group.color)

  // Same three facts, told apart by key rather than by a code plus a field name. `stale` used
  // to be inferred from `field === 'if-match'` and `taken` from "a conflict that was not
  // stale", so a third conflict the server learned to raise silently became "taken".
  const stale = issueIs(update.error, 'groups.changed_while_editing')
  const taken = issueIs(update.error, 'groups.name_taken')
  const error = taken ? t.create.taken : update.isError && !stale ? messageOf(t, update.error) : null
  const unchanged = name.trim() === group.name && icon === group.icon && color === group.color

  return (
    <Dialog
      titleId={titleId}
      icon={<PencilLine size={14} />}
      title={t.edit.title(group.name)}
      lead={t.edit.lead}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          update.mutate(
            {
              id: group.id,
              version: group.version,
              ...(name.trim() !== group.name ? { name: name.trim() } : {}),
              ...(icon !== group.icon ? { icon } : {}),
              ...(color !== group.color ? { color } : {})
            },
            { onSuccess: onClose }
          )
        }}
      >
        <DialogBody>
          {stale && <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={t.edit.stale} />}
          {update.isError && !stale && !taken && (
            <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={messageOf(t, update.error)} />
          )}
          <IdentityFields
            name={name}
            icon={icon}
            color={color}
            onName={setName}
            onIcon={setIcon}
            onColor={setColor}
            error={error}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t.create.cancel}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={update.isPending || unchanged || name.trim() === '' || stale}
          >
            {update.isPending ? t.edit.submitting : t.edit.submit}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Managers and members
// ---------------------------------------------------------------------------

export const AppointManagerDialog = ({
  group,
  managerIds,
  onClose
}: {
  group: GroupRef
  managerIds: readonly string[]
  onClose: () => void
}) => {
  const t = useI18n().t.groups
  const titleId = useId()
  const appoint = useAppointManager(group.id)
  const [person, setPerson] = useState<PersonRef[]>([])
  const chosen = person[0]

  return (
    <Dialog
      titleId={titleId}
      icon={<Crown size={14} />}
      title={t.appoint.title(group.name)}
      lead={t.appoint.lead(group.name)}
      onClose={onClose}
    >
      <DialogBody>
        {appoint.isError && (
          <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={messageOf(t, appoint.error)} />
        )}
        <div>
          <p className="mb-1.5 text-[13px] font-semibold">{t.appoint.person}</p>
          {chosen ? (
            <div className="flex items-center justify-between gap-2 rounded-tile border border-rule-strong bg-card-solid px-3 py-2">
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-semibold">{chosen.displayName}</span>
                <span className="block truncate text-[12px] text-fg-muted">{chosen.email}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => setPerson([])}>
                {t.picker.remove(chosen.displayName)}
              </Button>
            </div>
          ) : (
            <PersonPicker chosen={person} onChange={setPerson} exclude={managerIds} autoFocus />
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t.create.cancel}
        </Button>
        <Button
          variant="primary"
          disabled={!chosen || appoint.isPending}
          onClick={() => chosen && appoint.mutate(chosen.id, { onSuccess: onClose })}
        >
          {appoint.isPending ? t.appoint.submitting : t.appoint.submit}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export const AddPeopleDialog = ({
  group,
  access,
  onClose
}: {
  group: GroupRef
  /** What membership reaches, when the reader may see it: bases and agents shared with the group. */
  access: { bases: number; agents: number } | null
  onClose: () => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.groups
  const titleId = useId()
  const add = useAddGroupMembers(group.id)
  const [people, setPeople] = useState<PersonRef[]>([])

  const lead =
    access === null ? t.add.leadUnknown(group.name) : t.add.lead(fmt, group.name, access.bases, access.agents)
  const result = add.data

  return (
    <Dialog
      titleId={titleId}
      icon={<UserPlus size={14} />}
      title={t.add.title(group.name)}
      lead={lead}
      onClose={onClose}
    >
      <DialogBody>
        {add.isError && (
          <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={messageOf(t, add.error)} />
        )}
        {!result && <PersonPicker chosen={people} onChange={setPeople} multiple autoFocus />}
        {/* Mounted empty, so the result is announced the moment it lands; last, so while empty it adds no space. */}
        <div role="status" className="space-y-2 text-[13px] leading-[1.55]">
          {result && (
            <>
              <p>{t.add.result(fmt, result.added.length, result.already.length)}</p>
              {result.refused.length > 0 && (
                <ul className="space-y-1 text-alert">
                  {result.refused.map((refusal) => {
                    const person = people.find((p) => p.id === refusal.id)
                    return (
                      <li key={refusal.id}>
                        {person?.displayName ?? refusal.id} {t.add.refused[refusal.reason]}
                      </li>
                    )
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        {result ? (
          <Button variant="primary" onClick={onClose}>
            {t.add.done}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t.create.cancel}
            </Button>
            <Button
              variant="primary"
              disabled={people.length === 0 || add.isPending}
              onClick={() => add.mutate(people.map((person) => person.id))}
            >
              {add.isPending ? t.add.submitting : t.add.submit(fmt, people.length)}
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  )
}

/**
 * Removing somebody from a group. The wording is deliberately conservative: it says what this
 * group gave them and nothing about any other group they are in — the remover may not be allowed
 * to know (D-7).
 */
export const RemoveMemberDialog = ({
  group,
  person,
  onClose
}: {
  group: GroupRef
  person: { id: string; displayName: string }
  onClose: () => void
}) => {
  const t = useI18n().t.groups
  const titleId = useId()
  const remove = useRemoveGroupMember(group.id)
  return (
    <Dialog
      titleId={titleId}
      icon={<UserMinus size={14} />}
      title={t.remove.title(person.displayName, group.name)}
      lead={t.remove.body(group.name)}
      onClose={onClose}
    >
      {remove.isError && (
        <DialogBody>
          <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={messageOf(t, remove.error)} />
        </DialogBody>
      )}
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t.create.cancel}
        </Button>
        <Button
          variant="danger"
          disabled={remove.isPending}
          onClick={() => remove.mutate(person.id, { onSuccess: onClose })}
        >
          {remove.isPending ? t.remove.submitting : t.remove.submit}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export const RemoveManagerDialog = ({
  group,
  person,
  onClose
}: {
  group: GroupRef
  person: { id: string; displayName: string }
  onClose: () => void
}) => {
  const t = useI18n().t.groups
  const titleId = useId()
  const remove = useRemoveManager(group.id)
  return (
    <Dialog
      titleId={titleId}
      icon={<UserMinus size={14} />}
      title={t.removeManager.title(person.displayName, group.name)}
      lead={t.removeManager.body(group.name)}
      onClose={onClose}
    >
      {remove.isError && (
        <DialogBody>
          <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={messageOf(t, remove.error)} />
        </DialogBody>
      )}
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t.create.cancel}
        </Button>
        <Button
          variant="danger"
          disabled={remove.isPending}
          onClick={() => remove.mutate(person.id, { onSuccess: onClose })}
        >
          {remove.isPending ? t.removeManager.submitting : t.removeManager.submit}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Deleting a group (D-26)
// ---------------------------------------------------------------------------

export const DeleteGroupDialog = ({ group, onClose }: { group: GroupRef; onClose: () => void }) => {
  const fmt = useI18n()
  const t = fmt.t.groups
  const titleId = useId()
  const nameId = useId()
  const navigate = useNavigate()
  const session = useSession()
  const me = session.data?.user
  const preview = useGroupDeletionPreview(group.id)
  const remove = useDeleteGroup(group.id)
  const [typed, setTyped] = useState('')
  const [owner, setOwner] = useState<OwnerChoice | null>(me ? { kind: 'user', id: me.id, name: me.displayName } : null)

  const data = preview.data
  const owns = data ? data.owns.knowledgeBases.length + data.owns.agents.length : 0
  const drifted = remove.error instanceof ApiRequestError && remove.error.code === 'conflict'
  const nameMatches = typed.trim().toLowerCase() === group.name.trim().toLowerCase()

  const submit = () => {
    if (!data) return
    remove.mutate(
      {
        confirmName: typed.trim(),
        digest: data.digest,
        ...(owns > 0 && owner ? { newOwner: ownerRefOf(owner) } : {})
      },
      {
        onSuccess: () => {
          onClose()
          navigate('/groups')
        },
        onError: (error) => {
          if (error instanceof ApiRequestError && error.code === 'conflict') void preview.refetch()
        }
      }
    )
  }

  return (
    <Dialog
      titleId={titleId}
      icon={<Trash2 size={14} />}
      title={t.deletion.title(group.name)}
      lead={t.deletion.lead}
      onClose={onClose}
      width="lg"
    >
      <DialogBody>
        {preview.isLoading && (
          <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
            <Spinner /> {t.deletion.loading}
          </p>
        )}
        {/* Nothing to agree to without the consequences: said, with the way to ask again. */}
        {preview.isError && (
          <p role="alert" className="flex flex-wrap items-center gap-2 text-pretty text-[12.5px] text-alert">
            {t.deletion.previewFailed}
            <Button size="sm" variant="ghost" onClick={() => void preview.refetch()}>
              {t.detail.retry}
            </Button>
          </p>
        )}
        {drifted && <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={t.deletion.drifted} />}
        {remove.isError && !drifted && (
          <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={messageOf(t, remove.error)} />
        )}

        {data && (
          <>
            <ul className="space-y-1 text-[13px] leading-[1.55]">
              <li>{t.deletion.members(fmt, data.members)}</li>
              {data.managers > 0 && <li>{t.deletion.managers(fmt, data.managers)}</li>}
              {(data.pendingRequests.sharing > 0 || data.pendingRequests.lending > 0) && (
                <li>{t.deletion.requests(fmt, data.pendingRequests.sharing, data.pendingRequests.lending)}</li>
              )}
            </ul>

            <section>
              <h3 className="eyebrow mb-1.5">{t.deletion.sharedWith}</h3>
              {data.sharedWith.knowledgeBases.length +
                data.sharedWith.agents.length +
                data.sharedWith.connectedSystems.length +
                data.sharedWith.remoteAgents.length ===
              0 ? (
                <p className="text-[12.5px] text-fg-muted">{t.deletion.nothingShared}</p>
              ) : (
                <ul className="space-y-1 text-[12.5px]">
                  {[...data.sharedWith.knowledgeBases, ...data.sharedWith.agents].map((item) => (
                    // Inline rather than flex, so a long line wraps between words and never
                    // starts the next line on the dash.
                    <li key={item.id}>
                      <span className="font-medium">{item.name}</span>{' '}
                      <span className="text-fg-muted">
                        — {item.fallsBackToMaintainers ? t.deletion.fallsBack : t.deletion.staysShared}
                      </span>
                    </li>
                  ))}
                  {[...data.sharedWith.connectedSystems, ...data.sharedWith.remoteAgents].map((item) => (
                    <li key={item.id}>
                      <span className="font-medium">{item.name}</span>{' '}
                      <span className={item.becomesUnusable ? 'text-alert' : 'text-fg-muted'}>
                        — {item.becomesUnusable ? t.deletion.becomesUnusable : t.deletion.staysAvailable}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className="eyebrow mb-1.5">{owns === 0 ? t.deletion.owns : t.deletion.ownsChoose}</h3>
              {owns === 0 ? (
                <p className="text-[12.5px] text-fg-muted">{t.deletion.nothingOwned}</p>
              ) : (
                <>
                  <ul className="mb-2 space-y-1 text-[12.5px]">
                    {data.owns.knowledgeBases.map((item) => (
                      <li key={item.id} className="font-medium">
                        {item.name}
                      </li>
                    ))}
                    {data.owns.agents.map((item) => (
                      <li key={item.id} className="font-medium">
                        {item.name}
                      </li>
                    ))}
                  </ul>
                  <p className="mb-2 text-[12px] text-fg-subtle">{t.deletion.ownsLead}</p>
                  {me && (
                    <OwnerPicker
                      value={owner}
                      onChange={setOwner}
                      viewer={{
                        id: me.id,
                        displayName: me.displayName,
                        managedGroups: me.managedGroups.filter((managed) => managed.id !== group.id)
                      }}
                      current={{ kind: 'group', id: group.id }}
                      allowAnyone
                    />
                  )}
                </>
              )}
            </section>

            <div>
              <label htmlFor={nameId} className="mb-1 block text-[13px] font-semibold">
                {t.deletion.typeName(group.name)}
              </label>
              <Input id={nameId} value={typed} autoComplete="off" onChange={(event) => setTyped(event.target.value)} />
            </div>
          </>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t.create.cancel}
        </Button>
        <Button
          variant="danger"
          disabled={!data || !nameMatches || remove.isPending || (owns > 0 && owner === null)}
          onClick={submit}
        >
          {remove.isPending ? t.deletion.submitting : t.deletion.submit}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
